// Singleton dispatcher for the in-app prompt dialog.
// Replaces `window.prompt(...)` everywhere so the input field is rendered
// with the design system (shadcn Dialog + Input) instead of the native
// browser popup. Same return contract as `window.prompt` : the resolved
// value is `string` when the user validates and `null` when they cancel.

export type PromptInputType = "text" | "url" | "email";

export interface PromptDialogOptions {
  title: string;
  label?: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: PromptInputType;
  /** Return an error message to display, or null when the value is valid. */
  validate?: (value: string) => string | null;
}

export interface PromptDialogRequest extends PromptDialogOptions {
  id: number;
  resolve: (value: string | null) => void;
}

type Listener = (req: PromptDialogRequest | null) => void;

let listener: Listener | null = null;
let nextId = 1;
let current: PromptDialogRequest | null = null;

export function subscribePromptDialog(fn: Listener): () => void {
  listener = fn;
  if (current) fn(current);
  return () => {
    if (listener === fn) listener = null;
  };
}

export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  if (current) {
    current.resolve(null);
    current = null;
  }
  return new Promise<string | null>((resolve) => {
    current = {
      ...options,
      id: nextId++,
      resolve: (v) => {
        current = null;
        listener?.(null);
        resolve(v);
      },
    };
    if (listener) {
      listener(current);
    } else {
      console.warn("[promptDialog] No host mounted, defaulting to null.");
      const c = current;
      current = null;
      resolve(null);
      void c;
    }
  });
}
